import axios from 'axios';
import { useState, useEffect, useRef } from 'react';
import {
   Button,
   Container,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   IconButton,
   Grid,
   Select,
   MenuItem,
   FormControl,
   InputLabel,
   FormHelperText,
   CircularProgress,
   Typography,
   Box,
} from '@mui/material';
import { Delete, CloudUpload, Refresh, Close, Add } from '@mui/icons-material';
import APIInstance from '../../Web/Instance';
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery';
import { API_URL } from '../../Config';
import { toast } from 'react-toastify';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClassicEditor from '../../Utils/RichEditor';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import InfiniteAutocomplete from '../../Components/InfiniteAutocomplete';

export default function EditProposal() {
   const { id } = useParams();
   const [pages, setPages] = useState([]);
   const [headerText, setHeaderText] = useState('');
   const [headerSubText, setHeaderSubText] = useState('');
   const [proposalName, setProposalName] = useState('');
   const [openLayoutDialog, setOpenLayoutDialog] = useState(false);
   const [opportunities, setOpportunities] = useState([]);
   const [selectedOpportunity, setSelectedOpportunity] = useState('');
   const [isGenerateButtonDisabled, setIsGenerateButtonDisabled] = useState(false);
   const [isLoading, setIsLoading] = useState(true);
   const navigate = useNavigate();

   // State variables for infinite scroll
   const [search, setSearch] = useState('');
   const [page, setPage] = useState(1);
   const [visibleOptions, setVisibleOptions] = useState([]);
   const [selectedOption, setSelectedOption] = useState(null);
   const debounceTimeout = useRef(null);

   // State variables for quotations and estimates
   const [quotations, setQuotations] = useState([]);
   const [estimates, setEstimates] = useState([]);
   const [selectedQuotation, setSelectedQuotation] = useState('');
   const [selectedEstimate, setSelectedEstimate] = useState('');

   // New state variables for additional page types
   const [selectedTechnologies, setSelectedTechnologies] = useState([]);
   const [techSections, setTechSections] = useState([{ id: Date.now(), technologies: [] }]);
   const [technologyOptions, setTechnologyOptions] = useState([]);
   const [fileInputKey, setFileInputKey] = useState(Date.now());
   const [bannerImage, setBannerImage] = useState(null);

   // Define layouts once
   const layoutOptions = [
      { label: 'Left Image - Right Text', value: 'leftImage-rightText' },
      { label: 'Right Image - Left Text', value: 'rightImage-leftText' },
      { label: 'No Image - Only Text', value: 'noImage-OnlyText' },
      { label: 'USP (Heading & Title)', value: 'usp' },
      { label: 'Tech Stack (Title & Image)', value: 'techStack' },
      { label: 'Team (Name, Members, Allocation)', value: 'team' },
      { label: 'Project Overview', value: 'project-overview' },
   ];

   // Fetch proposal data
   useEffect(() => {
      const fetchProposalData = async () => {
         try {
            const token = getAuthToken();
            const response = await APIInstance.get(`${API_URL}proposal/${id}`, {
               headers: {
                  Authorization: `Bearer ${token}`,
                  token,
               },
            });

            const proposalData = response.data.response;

            // Set the basic proposal data
            setProposalName(proposalData.title || '');
            setHeaderText(proposalData.headerText || '');
            setHeaderSubText(proposalData.headerSubText || '');

            // Set selected opportunity
            if (proposalData.opportunity?.id) {
               setSelectedOpportunity(proposalData.opportunity.id);
               setSelectedOption({
                  id: proposalData.opportunity.id,
                  name: proposalData.opportunity.name,
               });
            }

            // Set selected quotation and estimate
            if (proposalData.quotation?.id) {
               setSelectedQuotation(proposalData.quotation.id);
            }

            if (proposalData.estimate?.id) {
               setSelectedEstimate(proposalData.estimate.id);
            }

            // Set pages data if available
            if (proposalData.pages && proposalData.pages.length > 0) {
               // Filter out null values from the pages array
               const validPages = proposalData.pages.filter((page) => page !== null);

               // Map images to their corresponding pages
               const imagePaths = proposalData.ImagePaths || [];

               const formattedPages = validPages.map((page, index) => {
                  // Find matching image if exists
                  const pageNumber = index + 1;
                  const imageData = imagePaths.find(
                     (img) => parseInt(img.pageNumber) === pageNumber,
                  );

                  const newPage = {
                     id: pageNumber,
                     layout: page.layout || 'noImage-OnlyText',
                     header: page.header || '',
                     text: page.text || '',
                     image: null, // We don't prefill images but we could show the path
                     imagePath: imageData ? imageData.image : null,
                  };

                  // Include existing structured content
                  if (page.uspContent) newPage.uspContent = page.uspContent;
                  if (page.techStack) newPage.techStack = page.techStack;
                  if (page.teamMembers) {
                     newPage.teamMembers = page.teamMembers.map((section) => ({
                        name: section.name,
                        members: Array.isArray(section.members)
                           ? section.members.map((m) => ({
                                role: m.position || m.role || '',
                                members: m.members || '',
                                allocation: m.allocation || '',
                                image: null,
                                imagePath: null,
                             }))
                           : [],
                     }));
                  }

                  return newPage;
               });

               setPages(formattedPages);

               // Prefill banner image preview
               if (proposalData.bannerImage) {
                  setBannerImage({ preview: `${API_URL}${proposalData.bannerImage}` });
               }
            }

            setIsLoading(false);
         } catch (error) {
            console.error('Error fetching proposal data:', error);
            toast.error('Error loading proposal data. Please try again.');
            setIsLoading(false);
            navigate('/proposal'); // Redirect back to list if there's an error
         }
      };

      fetchProposalData();
   }, [id, navigate]);

   // Fetch opportunities
   useEffect(() => {
      const fetchOpportunities = async () => {
         try {
            const token = getAuthToken();
            const response = await APIInstance.get(`${API_URL}opportunity/list`, {
               headers: {
                  Authorization: `Bearer ${token}`,
                  token,
               },
               params: {
                  limit: 10,
                  name: search,
                  page: page,
               },
            });

            const newData = response.data?.response?.data || [];

            // Add new opportunities to visible options (removing duplicates)
            setVisibleOptions((prevOptions) => {
               const existingIds = new Set(prevOptions.map((opt) => opt.id));
               const uniqueNewData = newData.filter((opt) => !existingIds.has(opt.id));
               return [...prevOptions, ...uniqueNewData];
            });

            setOpportunities(response.data?.response?.data);
         } catch (error) {
            console.log('Error fetching opportunities', error);
            toast.error('Error fetching opportunities. Please try again later.');
         }
      };
      fetchOpportunities();
   }, [search, page]);

   // Fetch quotations
   useEffect(() => {
      const fetchQuotations = async () => {
         try {
            const token = getAuthToken();
            const response = await APIInstance.get(`${API_URL}quotation`, {
               headers: {
                  Authorization: `Bearer ${token}`,
                  token,
               },
            });
            // Ensure quotations is always an array
            const quotationsData = response.data?.response.data;
            setQuotations(Array.isArray(quotationsData) ? quotationsData : []);
         } catch (error) {
            console.error('Error fetching quotations:', error);
            toast.error('Failed to fetch quotations');
            setQuotations([]); // Set to empty array on error
         }
      };
      fetchQuotations();
   }, []);

   // Fetch estimates
   useEffect(() => {
      const fetchEstimates = async () => {
         try {
            const token = getAuthToken();
            const response = await APIInstance.get(`${API_URL}estimate`, {
               headers: {
                  Authorization: `Bearer ${token}`,
                  token,
               },
            });
            // Ensure estimates is always an array
            const estimatesData = response.data?.response;
            setEstimates(Array.isArray(estimatesData) ? estimatesData : []);
         } catch (error) {
            console.error('Error fetching estimates:', error);
            toast.error('Failed to fetch estimates');
            setEstimates([]); // Set to empty array on error
         }
      };
      fetchEstimates();
   }, []);

   // Fetch technology stack list
   useEffect(() => {
      const fetchTech = async () => {
         try {
            const token = getAuthToken();
            const resp = await APIInstance.get(`${API_URL}technology-stack/list-technology`, {
               headers: { Authorization: `Bearer ${token}`, token },
            });
            setTechnologyOptions(resp.data?.response || []);
         } catch (e) {
            console.error('Failed to load technology options', e);
         }
      };
      fetchTech();
   }, []);

   const handleScroll = (event) => {
      const listboxNode = event.currentTarget;
      const bottom =
         listboxNode.scrollHeight - listboxNode.scrollTop <= listboxNode.clientHeight + 5;

      if (bottom) {
         setPage((prev) => prev + 1);
      }
   };

   const handleSelectOption = (event, value, reason) => {
      setSelectedOption(value);
      setSelectedOpportunity(reason === 'selectOption' ? value.id : '');
   };

   const handleSearch = (event, value) => {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => {
         setSearch(value);
         setPage(1); // Reset page when search changes
         setVisibleOptions([]); // Clear previous results
      }, 300);
   };

   const handleAddPage = () => setOpenLayoutDialog(true);

   const handleSetLayout = (layout) => {
      const newPage = {
         id: Date.now(),
         layout,
         header: '',
         text: '',
         image: null,
      };

      // Initialize specific fields based on layout type
      if (layout === 'usp') {
         newPage.uspContent = [{ heading: '', title: '' }];
      } else if (layout === 'techStack') {
         newPage.techStack = [{ name: 'Option 1', stacks: [] }];
      } else if (layout === 'team') {
         newPage.teamMembers = [
            {
               name: `Option ${pages.filter((p) => p.layout === 'team').length + 1}`,
               members: [{ position: '', members: '', allocation: '', image: null }],
            },
         ];
      }

      setPages([...pages, newPage]);
      setOpenLayoutDialog(false);
   };

   const handleChange = (id, field, value) => {
      setPages(pages?.map((page) => (page.id === id ? { ...page, [field]: value } : page)));
   };

   const handleImageUpload = (id, e) => {
      const file = e.target.files[0];
      if (file) {
         setPages(
            pages?.map((page) =>
               page.id === id
                  ? {
                       ...page,
                       image: file,
                       imagePath: null, // Clear imagePath when new image is uploaded
                    }
                  : page,
            ),
         );
      }
   };

   const handleResetImage = (id) => {
      const updatedPage = pages?.map((page) =>
         page.id === id ? { ...page, image: null, imagePath: null } : page,
      );
      setPages(updatedPage);
   };

   const handleDeletePage = (id) => {
      const updatedPages = pages.filter((page) => page.id !== id);
      setPages(updatedPages);
   };

   const handleQuotationChange = (e) => {
      setSelectedQuotation(e.target.value);
   };

   const handleEstimateChange = (e) => {
      setSelectedEstimate(e.target.value);
   };

   const handleUspChange = (pageId, index, field, value) => {
      setPages(
         pages.map((page) => {
            if (page.id === pageId) {
               const uspContent = [...(page.uspContent || [])];
               uspContent[index] = {
                  ...uspContent[index],
                  [field]: value,
               };
               return { ...page, uspContent };
            }
            return page;
         }),
      );
   };

   const handleAddUspField = (pageId) => {
      setPages(
         pages.map((page) => {
            if (page.id === pageId) {
               const uspContent = [...(page.uspContent || []), { heading: '', title: '' }];
               return { ...page, uspContent };
            }
            return page;
         }),
      );
   };

   const handleRemoveUspField = (pageId, indexToRemove) => {
      setPages(
         pages.map((page) => {
            if (page.id === pageId) {
               const uspContent = (page.uspContent || []).filter(
                  (_, index) => index !== indexToRemove,
               );
               return { ...page, uspContent };
            }
            return page;
         }),
      );
   };

   const handleAddTechnology = (pageId, sectionIndex, technologyId) => {
      setPages(
         pages.map((page) => {
            if (page.id === pageId) {
               const techStack = [...(page.techStack || [])];
               // If section doesn't exist, create it
               if (!techStack[sectionIndex]) {
                  techStack[sectionIndex] = { name: `Section ${sectionIndex + 1}`, stacks: [] };
               }
               // Add technology name if not already in the stacks
               const techName = technologyOptions.find(
                  (opt) => opt.id === technologyId,
               )?.technology;
               if (techName && !techStack[sectionIndex].stacks.includes(techName)) {
                  techStack[sectionIndex] = {
                     ...techStack[sectionIndex],
                     stacks: [...techStack[sectionIndex].stacks, techName],
                  };
               }
               return { ...page, techStack };
            }
            return page;
         }),
      );
   };

   const handleRemoveTechnology = (pageId, sectionIndex, techToRemove) => {
      setPages(
         pages.map((page) => {
            if (page.id === pageId) {
               const techStack = [...(page.techStack || [])];
               if (techStack[sectionIndex]) {
                  techStack[sectionIndex] = {
                     ...techStack[sectionIndex],
                     stacks: techStack[sectionIndex].stacks.filter((tech) => tech !== techToRemove),
                  };
               }
               return { ...page, techStack };
            }
            return page;
         }),
      );
   };

   const handleAddTechSection = (pageId) => {
      setPages(
         pages.map((page) => {
            if (page.id === pageId) {
               const techStack = [
                  ...(page.techStack || []),
                  {
                     name: `Option ${(page.techStack || []).length + 1}`,
                     stacks: [],
                  },
               ];
               return { ...page, techStack };
            }
            return page;
         }),
      );
   };

   const handleRemoveTechSection = (pageId, sectionIndex) => {
      setPages(
         pages.map((page) => {
            if (page.id === pageId) {
               const techStack = (page.techStack || []).filter(
                  (_, index) => index !== sectionIndex,
               );
               return { ...page, techStack };
            }
            return page;
         }),
      );
   };

   const handleBannerImageChange = (e) => {
      const file = e.target.files[0];
      if (file) {
         setBannerImage({ file, preview: URL.createObjectURL(file) });
      }
   };

   const handleTeamMemberChange = (pageId, sectionIndex, memberIndex, field, value) => {
      setPages(
         pages.map((p) =>
            p.id === pageId
               ? {
                    ...p,
                    teamMembers: p.teamMembers.map((sec, sIdx) =>
                       sIdx !== sectionIndex
                          ? sec
                          : {
                               ...sec,
                               members: sec.members.map((m, mIdx) =>
                                  mIdx !== memberIndex ? m : { ...m, [field]: value },
                               ),
                            },
                    ),
                 }
               : p,
         ),
      );
   };

   const handleAddTeamMember = (pageId, sectionIndex) => {
      setPages(
         pages.map((p) =>
            p.id === pageId
               ? {
                    ...p,
                    teamMembers: p.teamMembers.map((sec, sIdx) =>
                       sIdx !== sectionIndex
                          ? sec
                          : {
                               ...sec,
                               members: [
                                  ...sec.members,
                                  { name: '', role: '', members: '', allocation: '', image: null },
                               ],
                            },
                    ),
                 }
               : p,
         ),
      );
   };

   const handleRemoveTeamMember = (pageId, sectionIndex, memberIndex) => {
      setPages(
         pages.map((p) =>
            p.id === pageId
               ? {
                    ...p,
                    teamMembers: p.teamMembers.map((sec, sIdx) =>
                       sIdx !== sectionIndex
                          ? sec
                          : {
                               ...sec,
                               members: sec.members.filter((_, i) => i !== memberIndex),
                            },
                    ),
                 }
               : p,
         ),
      );
   };

   async function downloadPDF(url, fileName) {
      try {
         let newUrl = `${process.env.REACT_APP_API_URL}/downloads/${fileName}`;
         url = new URL(newUrl).href; // Ensure correct format
         const response = await axios.get(url, {
            responseType: 'blob', // Ensures we receive a binary file
         });
         const blobUrl = URL.createObjectURL(response.data);
         const a = document.createElement('a');
         a.href = blobUrl;
         a.download = `updated_proposal_${fileName}` || 'updated_proposal.pdf';
         document.body.appendChild(a); // Append to body
         a.click(); // Trigger the download
         document.body.removeChild(a); // Remove the element
         URL.revokeObjectURL(blobUrl); // Cleanup the URL object
         navigate('/proposal'); // Redirect to proposal list
      } catch (error) {
         toast.error('Error downloading the file. Please try again later.');
         console.error('Error downloading the file:', error);
      }
   }

   const handleSubmit = async () => {
      if (!selectedOpportunity) {
         toast.error('Please select an opportunity before updating the proposal');
         return;
      }
      if (!proposalName.trim()) {
         toast.error('Please enter a proposal name');
         return;
      }

      const formData = new FormData();
      formData.append('title', proposalName);
      formData.append('headerText', headerText);
      formData.append('headerSubText', headerSubText);
      formData.append('selectedOpportunity', String(parseInt(selectedOpportunity)));
      if (selectedQuotation)
         formData.append('selectedQuotation', String(parseInt(selectedQuotation)));
      if (selectedEstimate) formData.append('selectedEstimate', String(parseInt(selectedEstimate)));
      if (bannerImage?.file) formData.append('bannerImage', bannerImage.file);

      // prepare page payloads
      const payloadPages = pages.map((p, idx) => {
         const base = {
            layout: p.layout,
            header: p.header || '',
            text: p.text || '',
         };
         if (p.uspContent) base.uspContent = p.uspContent;
         if (p.techStack) base.techStack = p.techStack;
         if (p.teamMembers)
            base.teamMembers = p.teamMembers.map((sec) => ({
               name: sec.name,
               members: sec.members.map((m) => ({
                  position: m.role,
                  members: m.members,
                  allocation: m.allocation,
                  // images handled below
               })),
            }));
         return base;
      });

      formData.append('pagesCount', String(payloadPages.length));
      formData.append('pages', JSON.stringify(payloadPages));

      // append page images
      pages.forEach((p, idx) => {
         if (p.image instanceof File) {
            formData.append(`pages[${idx}][image]`, p.image);
         }
         // append team member images if any
         p.teamMembers?.forEach((sec, sIdx) => {
            sec.members.forEach((m, mIdx) => {
               if (m.image instanceof File) {
                  formData.append(
                     `pages[${idx}][teamMembers][${sIdx}][members][${mIdx}][image]`,
                     m.image,
                  );
               }
            });
         });
      });

      try {
         setIsGenerateButtonDisabled(true);
         const token = getAuthToken();
         const resp = await APIInstance.post(`${API_URL}proposal/update/${id}`, formData, {
            headers: {
               'Content-Type': 'multipart/form-data',
               Authorization: `Bearer ${token}`,
               token,
            },
         });
         toast.success('Proposal updated successfully');
         const pdfPath = resp.data?.response?.pdfPath;
         if (pdfPath) {
            // download if returned
            const fileName = resp.data.response.fileName;
            await downloadPDF(pdfPath, fileName);
         } else {
            navigate('/proposal');
         }
      } catch (err) {
         console.error(err);
         toast.error(
            err?.response?.data?.message || 'Unable to update proposal. Please try again later.',
         );
      } finally {
         setIsGenerateButtonDisabled(false);
      }
   };

   if (isLoading) {
      return (
         <Container
            maxWidth="lg"
            style={{
               display: 'flex',
               justifyContent: 'center',
               alignItems: 'center',
               height: '100vh',
            }}
         >
            <CircularProgress />
         </Container>
      );
   }

   return (
      <div className="content-wrapper">
         <div className="content-header">
            <div className="container-fluid">
               <div className="d-flex justify-content-between mb-2">
                  <div>
                     <h1 className="m-0 badge badge-dark">Edit Proposal</h1>
                  </div>
                  <div>
                     <Link to="/proposal" className="btn btn-dark">
                        <ArrowBackIcon /> Back
                     </Link>
                  </div>
               </div>
            </div>
         </div>
         <div className="card card-dark p-3 m-3">
            <Grid container spacing={2}>
               <Grid item xs={12}>
                  <TextField
                     required
                     fullWidth
                     label="Proposal Name"
                     value={proposalName}
                     onChange={(e) => setProposalName(e.target.value)}
                     error={!proposalName.trim()}
                     helperText={!proposalName.trim() ? 'Proposal name is required' : ''}
                     style={{ marginBottom: 20 }}
                  />
               </Grid>
               <Grid item xs={12}>
                  <InfiniteAutocomplete
                     value={selectedOption}
                     onChange={(event, value, reason) => handleSelectOption(event, value, reason)}
                     options={visibleOptions}
                     onScroll={handleScroll}
                     inputValue={search}
                     setInputValue={handleSearch}
                     total_options={'Opportunity'}
                     error={!selectedOpportunity}
                     helperText={!selectedOpportunity ? 'Please select an opportunity' : ''}
                  />
               </Grid>

               <Grid item xs={6}>
                  <FormControl fullWidth>
                     <InputLabel id="quotation-select-label">Quotation (Optional)</InputLabel>
                     <Select
                        labelId="quotation-select-label"
                        id="quotation-select"
                        value={selectedQuotation}
                        label="Quotation (Optional)"
                        onChange={handleQuotationChange}
                     >
                        <MenuItem value="">
                           <em>None</em>
                        </MenuItem>
                        {Array.isArray(quotations) &&
                           quotations.map((quotation) => (
                              <MenuItem key={quotation.id} value={quotation.id}>
                                 {quotation.title}
                              </MenuItem>
                           ))}
                     </Select>
                  </FormControl>
               </Grid>

               <Grid item xs={6}>
                  <FormControl fullWidth>
                     <InputLabel id="estimate-select-label">Estimate (Optional)</InputLabel>
                     <Select
                        labelId="estimate-select-label"
                        id="estimate-select"
                        value={selectedEstimate}
                        label="Estimate (Optional)"
                        onChange={handleEstimateChange}
                     >
                        <MenuItem value="">
                           <em>None</em>
                        </MenuItem>
                        {Array.isArray(estimates) &&
                           estimates.map((estimate) => (
                              <MenuItem key={estimate.id} value={estimate.id}>
                                 {estimate.name}
                              </MenuItem>
                           ))}
                     </Select>
                  </FormControl>
               </Grid>

               <Grid item xs={12}>
                  <TextField
                     fullWidth
                     label="Enter Header Text"
                     value={headerText}
                     onChange={(e) => setHeaderText(e.target.value)}
                     style={{ marginBottom: 20 }}
                  />
                  <TextField
                     fullWidth
                     label="Enter Header Sub text"
                     value={headerSubText}
                     onChange={(e) => setHeaderSubText(e.target.value)}
                     style={{ marginBottom: 20 }}
                  />
               </Grid>

               <Grid item xs={12}>
                  {/* Banner image upload & preview */}
                  <input
                     accept="image/*"
                     type="file"
                     id="banner-file-input"
                     style={{ display: 'none' }}
                     onChange={handleBannerImageChange}
                  />
                  <label htmlFor="banner-file-input">
                     <IconButton color="primary" component="span">
                        <CloudUpload />
                     </IconButton>
                     Upload Banner Image
                  </label>
                  {bannerImage && (
                     <>
                        <IconButton color="secondary" onClick={() => setBannerImage(null)}>
                           <Refresh />
                        </IconButton>
                        <Box mt={2}>
                           <img
                              src={bannerImage.preview}
                              alt="Banner preview"
                              style={{ width: '100%', maxHeight: 200, objectFit: 'cover' }}
                           />
                        </Box>
                     </>
                  )}
               </Grid>

               {/* --- begin pages render --- */}
               {pages.map((page) => (
                  <Grid item xs={12} key={page.id}>
                     <div
                        style={{
                           display: 'flex',
                           flexDirection:
                              page.layout === 'leftImage-rightText'
                                 ? 'row'
                                 : page.layout === 'rightImage-leftText'
                                   ? 'row-reverse'
                                   : 'column',
                           alignItems: page.layout === 'noImage-OnlyText' ? 'stretch' : 'center',
                           padding: 10,
                           border: '1px solid #ddd',
                           borderRadius: 8,
                           background: '#fff',
                           marginBottom: 20,
                           position: 'relative',
                        }}
                     >
                        {/* optional image upload */}
                        {['leftImage-rightText', 'rightImage-leftText'].includes(page.layout) && (
                           <div
                              style={{
                                 display: 'flex',
                                 flexDirection: 'column',
                                 alignItems: 'center',
                                 marginRight: page.layout === 'leftImage-rightText' ? 10 : 0,
                              }}
                           >
                              <input
                                 accept="image/*"
                                 type="file"
                                 style={{ display: 'none' }}
                                 id={`file-input-${page.id}`}
                                 onChange={(e) => handleImageUpload(page.id, e)}
                              />
                              <label htmlFor={`file-input-${page.id}`}>
                                 <IconButton color="primary" component="span">
                                    <CloudUpload fontSize="large" />
                                 </IconButton>
                              </label>
                              {(page.image || page.imagePath) && (
                                 <IconButton
                                    color="secondary"
                                    onClick={() => handleResetImage(page.id)}
                                 >
                                    <Refresh />
                                 </IconButton>
                              )}
                           </div>
                        )}

                        <div style={{ flex: 1, width: '100%' }}>
                           {/* Project Overview */}
                           {page.layout === 'project-overview' && (
                              <TextField
                                 label="Project Overview"
                                 fullWidth
                                 multiline
                                 rows={4}
                                 value={page.text || ''}
                                 onChange={(e) => handleChange(page.id, 'text', e.target.value)}
                                 style={{ marginBottom: 10 }}
                              />
                           )}

                           {/* Header + CKEditor */}
                           {[
                              'leftImage-rightText',
                              'rightImage-leftText',
                              'noImage-OnlyText',
                           ].includes(page.layout) && (
                              <>
                                 <TextField
                                    label="Header"
                                    fullWidth
                                    value={page.header || ''}
                                    onChange={(e) =>
                                       handleChange(page.id, 'header', e.target.value)
                                    }
                                    style={{ marginBottom: 10 }}
                                 />
                                 <div style={{ marginBottom: 10 }}>
                                    <label style={{ marginBottom: 5, display: 'block' }}>
                                       Text
                                    </label>
                                    <CKEditor
                                       editor={ClassicEditor}
                                       data={page.text || ''}
                                       onChange={(_e, editor) =>
                                          handleChange(page.id, 'text', editor.getData())
                                       }
                                    />
                                 </div>
                              </>
                           )}

                           {/* USP */}
                           {page.layout === 'usp' && (
                              <>
                                 {(page.uspContent || []).map((usp, idx) => (
                                    <Box
                                       key={idx}
                                       display="flex"
                                       gap={2}
                                       alignItems="center"
                                       mb={2}
                                    >
                                       <TextField
                                          label={`Heading ${idx + 1}`}
                                          fullWidth
                                          value={usp.heading}
                                          onChange={(e) =>
                                             handleUspChange(
                                                page.id,
                                                idx,
                                                'heading',
                                                e.target.value,
                                             )
                                          }
                                       />
                                       <TextField
                                          label={`Title ${idx + 1}`}
                                          fullWidth
                                          value={usp.title}
                                          onChange={(e) =>
                                             handleUspChange(page.id, idx, 'title', e.target.value)
                                          }
                                       />
                                       {page.uspContent.length > 1 && (
                                          <IconButton
                                             color="error"
                                             onClick={() => handleRemoveUspField(page.id, idx)}
                                          >
                                             <Delete />
                                          </IconButton>
                                       )}
                                    </Box>
                                 ))}
                                 <Button
                                    variant="outlined"
                                    startIcon={<Add />}
                                    onClick={() => handleAddUspField(page.id)}
                                 >
                                    Add More
                                 </Button>
                              </>
                           )}

                           {/* Tech Stack */}
                           {page.layout === 'techStack' && (
                              <Box p={2} border="1px solid grey" borderRadius={2} mb={2}>
                                 <Typography variant="h6" gutterBottom>
                                    Technology Stack
                                 </Typography>
                                 {(page.techStack || []).map((section, sIdx) => (
                                    <Box
                                       key={sIdx}
                                       p={2}
                                       border="1px dashed grey"
                                       borderRadius={2}
                                       mb={2}
                                    >
                                       <Typography variant="subtitle1">{section.name}</Typography>
                                       <Grid container spacing={2}>
                                          <Grid item xs={11}>
                                             <Select
                                                fullWidth
                                                displayEmpty
                                                onChange={(e) =>
                                                   handleAddTechnology(
                                                      page.id,
                                                      sIdx,
                                                      e.target.value,
                                                   )
                                                }
                                             >
                                                <MenuItem value="" disabled>
                                                   Select Technology
                                                </MenuItem>
                                                {technologyOptions.map((opt) => (
                                                   <MenuItem key={opt.id} value={opt.id}>
                                                      {opt.technology}
                                                   </MenuItem>
                                                ))}
                                             </Select>
                                          </Grid>
                                          <Grid item xs={12}>
                                             <Typography variant="subtitle2">
                                                Selected Technologies:
                                             </Typography>
                                             {section.stacks.map((tech, i) => (
                                                <Box
                                                   key={i}
                                                   display="flex"
                                                   alignItems="center"
                                                   mb={1}
                                                >
                                                   <Typography>{tech}</Typography>
                                                   <IconButton
                                                      size="small"
                                                      onClick={() =>
                                                         handleRemoveTechnology(page.id, sIdx, tech)
                                                      }
                                                   >
                                                      <Delete fontSize="small" />
                                                   </IconButton>
                                                </Box>
                                             ))}
                                          </Grid>
                                       </Grid>
                                       <Button
                                          variant="outlined"
                                          startIcon={<Add />}
                                          onClick={() => handleAddTechSection(page.id)}
                                       >
                                          Add Option
                                       </Button>
                                    </Box>
                                 ))}
                              </Box>
                           )}

                           {/* Team */}
                           {page.layout === 'team' && (
                              <>
                                 <Typography variant="h6" gutterBottom>
                                    Team Structure
                                 </Typography>
                                 {(page.teamMembers || []).map((section, secIdx) => (
                                    <Box key={secIdx} mb={4}>
                                       <Typography
                                          variant="subtitle1"
                                          fontWeight="bold"
                                          gutterBottom
                                       >
                                          {section.name}
                                       </Typography>
                                       {section.members.map((m, mIdx) => (
                                          <Box
                                             key={mIdx}
                                             sx={{
                                                border: '1px solid #ccc',
                                                borderRadius: 2,
                                                p: 2,
                                                mb: 2,
                                                position: 'relative',
                                             }}
                                          >
                                             <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                   <TextField
                                                      label="Role"
                                                      fullWidth
                                                      value={m.role}
                                                      onChange={(e) =>
                                                         handleTeamMemberChange(
                                                            page.id,
                                                            secIdx,
                                                            mIdx,
                                                            'role',
                                                            e.target.value,
                                                         )
                                                      }
                                                   />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                   <TextField
                                                      label="Members"
                                                      type="number"
                                                      fullWidth
                                                      value={m.members}
                                                      onChange={(e) =>
                                                         handleTeamMemberChange(
                                                            page.id,
                                                            secIdx,
                                                            mIdx,
                                                            'members',
                                                            e.target.value,
                                                         )
                                                      }
                                                   />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                   <TextField
                                                      label="Allocation"
                                                      fullWidth
                                                      value={m.allocation}
                                                      onChange={(e) =>
                                                         handleTeamMemberChange(
                                                            page.id,
                                                            secIdx,
                                                            mIdx,
                                                            'allocation',
                                                            e.target.value,
                                                         )
                                                      }
                                                   />
                                                </Grid>
                                             </Grid>
                                             <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() =>
                                                   handleRemoveTeamMember(page.id, secIdx, mIdx)
                                                }
                                                sx={{ position: 'absolute', top: 8, right: 8 }}
                                             >
                                                <Delete />
                                             </Button>
                                          </Box>
                                       ))}
                                       <Button
                                          variant="contained"
                                          startIcon={<Add />}
                                          onClick={() => handleAddTeamMember(page.id, secIdx)}
                                       >
                                          + Add Member
                                       </Button>
                                    </Box>
                                 ))}
                              </>
                           )}
                        </div>

                        {/* Delete page */}
                        <IconButton
                           onClick={() => handleDeletePage(page.id)}
                           color="error"
                           sx={{ position: 'absolute', top: 8, right: 8 }}
                        >
                           <Delete />
                        </IconButton>
                     </div>
                  </Grid>
               ))}
               {/* --- end pages render --- */}

               <Grid
                  item
                  xs={12}
                  style={{
                     display: 'flex',
                     justifyContent: 'center',
                     gap: '10px',
                     marginTop: '20px',
                  }}
               >
                  <Button onClick={handleAddPage} variant="contained" color="primary">
                     Add Page
                  </Button>
                  <Button
                     onClick={handleSubmit}
                     disabled={isGenerateButtonDisabled}
                     variant="contained"
                     color="secondary"
                  >
                     Update & Generate PDF
                  </Button>
               </Grid>
            </Grid>
         </div>
         <Dialog open={openLayoutDialog} onClose={() => setOpenLayoutDialog(false)}>
            <DialogTitle>Choose Layout Type</DialogTitle>
            <DialogContent>
               <Select
                  fullWidth
                  onChange={(e) => handleSetLayout(e.target.value)}
                  displayEmpty
                  defaultValue=""
               >
                  <MenuItem value="" disabled>
                     Select an option
                  </MenuItem>
                  {layoutOptions.map((option) => (
                     <MenuItem key={option.value} value={option.value}>
                        {option.label}
                     </MenuItem>
                  ))}
               </Select>
            </DialogContent>
            <DialogActions>
               <Button
                  onClick={() => setOpenLayoutDialog(false)}
                  color="primary"
                  startIcon={<Close />}
               >
                  Close
               </Button>
            </DialogActions>
         </Dialog>
      </div>
   );
}
